import {
	CircularProgress,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	useTheme,
} from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type { FC } from "react";
import type { ActionsDrawerVariant } from "web_myplann/Infrastructure/Themes/Colors";

export type DrawerAction = {
	key: string;
	label: string;
	icon: LucideIcon;
	onClick: () => void;
	isDestructive?: boolean;
	isPending?: boolean;
	disabled?: boolean;
};

type Props = {
	action: DrawerAction;
	isLast: boolean;
	variant: ActionsDrawerVariant;
};

export const ActionsDrawerRow: FC<Props> = ({ action, isLast, variant }) => {
	const theme = useTheme();
	const { iconSize } = theme.actionsDrawer;
	const variantConfig = theme.actionsDrawer[variant];

	const {
		label,
		icon: Icon,
		onClick,
		isDestructive,
		isPending,
		disabled,
	} = action;
	const isDisabled = disabled === true || isPending === true;

	const contentColor = isDisabled
		? theme.palette.grey[400]
		: isDestructive
			? variantConfig.destructiveColor
			: undefined;

	const iconColor = isDisabled
		? theme.palette.grey[300]
		: isDestructive
			? variantConfig.destructiveColor
			: variantConfig.iconColor;

	return (
		<ListItemButton
			disableRipple
			disabled={isDisabled}
			divider={variantConfig.hasDivider && !isLast}
			onClick={(event) => {
				event.stopPropagation();
				onClick();
			}}
			sx={{
				gap: theme.spacingTokens.md,
				px: theme.spacingTokens.lg,
				py: theme.spacingTokens.md,
				minHeight: variantConfig.rowMinHeight,
				borderColor: theme.palette.surface.border,
				"&:active": { backgroundColor: variantConfig.pressedBackground },
				"&.Mui-disabled": { opacity: 1 },
			}}
		>
			<ListItemIcon sx={{ minWidth: 0 }}>
				{isPending ? (
					<CircularProgress size={iconSize} />
				) : (
					<Icon size={iconSize} color={iconColor} />
				)}
			</ListItemIcon>
			<ListItemText
				primary={label}
				slotProps={{ primary: { variant: "bodyMedium", color: contentColor } }}
				sx={{ my: 0 }}
			/>
		</ListItemButton>
	);
};
